/**
 * "This interview was cancelled" — the tag, and the block for cancellations
 * that have nowhere else to appear.
 *
 * A candidate who cancels used to leave the board for good: the board decides
 * "already scheduled" from Lever's panels, and a cancellation does not reliably
 * remove one (see lib/cancelledInterviews.js). They come back now, and they have
 * to come back LEGIBLY — a re-surfaced candidate that looks like any other is a
 * recruiter re-proposing times without knowing the last set was declined.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import type { CancelledCandidate, InterviewCancellation } from '../../types/taActionBoard';
import { COLORS } from './format';
import { PhoneChip } from './PhoneChip';

const TZ = 'Europe/Berlin';
const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        timeZone: TZ,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

/** "Jonas cancelled" vs "we cancelled" — different situations, so both are named. */
function whoCancelled(c: InterviewCancellation): string {
  if (c.source === 'finn') return c.by ? `cancelled by ${c.by.split('@')[0]}` : 'cancelled by FINN';
  return 'cancelled by the candidate';
}

/**
 * The inline tag for a card in a scheduling section.
 *
 * The REASON is on the tag, not behind a hover: it is the whole reason this
 * card is here, and it changes what the recruiter does next ("took another
 * offer" is not "was ill").
 */
export function CancellationTag({ cancellation }: { cancellation: InterviewCancellation }) {
  const was = when(cancellation.previousStart);
  return (
    <span
      className="inline-flex flex-wrap items-baseline gap-x-1 rounded px-1.5 py-0.5 text-xs"
      style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
    >
      <span className="font-semibold">Interview cancelled</span>
      <span>· {whoCancelled(cancellation)}</span>
      {was && <span>· was {was}</span>}
      {cancellation.reason && <span>· “{cancellation.reason}”</span>}
      {!cancellation.reason && <span>· no reason given</span>}
    </span>
  );
}

/**
 * Cancellations with no scheduling section of their own, on the Feedback tab.
 *
 * The opening rounds (First / Personal Interview) have only a BULK invite
 * section, and a bulk invite is the wrong answer for one person who cancelled —
 * they need a conversation. So they are listed here, where a recruiter already
 * looks for "who needs me" (Theresa, Sep 2026).
 */
export function CancelledSection({
  candidates,
  onOpenLever,
}: {
  candidates: CancelledCandidate[];
  /** Open the candidate in Lever — the re-invite happens in their pipeline. */
  onOpenLever?: (candidate: CancelledCandidate) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      style={{ borderColor: COLORS.redText, backgroundColor: COLORS.redBg }}
    >
      <p className="text-sm font-semibold" style={{ color: COLORS.redText }}>
        {candidates.length} cancelled interview{candidates.length === 1 ? '' : 's'} — needs
        re-scheduling
      </p>
      <p className="text-xs" style={{ color: COLORS.redText }}>
        These candidates cancelled and have nothing booked. Their stage has no scheduling section of
        its own, so they are listed here rather than nowhere.
      </p>
      <ul className="space-y-2">
        {candidates.map((c) => (
          <li key={c.opportunityId} className="rounded-md bg-white p-2.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-gray-900">{c.name}</span>
              <span className="text-xs text-gray-500">
                {c.stageName} · waiting {c.waitingDays}d
              </span>
              <PhoneChip phone={c.phone} />
              <span className="ml-auto flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                                    onClick={() =>
                    onOpenLever
                      ? onOpenLever(c)
                      : // noopener: without it the opened page can reach back
                        // through window.opener and navigate the board tab.
                        window.open(c.leverUrl, '_blank', 'noopener,noreferrer')
                  }
                >
                  Open in Lever
                </Button>
              </span>
            </div>
            <div className="mt-1.5">
              <CancellationTag cancellation={c.cancellation} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
